import { ChangeDetectionStrategy, Component, computed, effect, input, model, output, untracked, viewChild } from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { IntegerFormControl } from 'forms-data';
import { NumberInputComponent, provideValueAccessor } from 'forms-ui';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'ideal-cart-stepper-input',
  imports: [MatIconModule, MatButtonModule, NumberInputComponent, ReactiveFormsModule],
  standalone: true,
  templateUrl: './cart-stepper-input.component.html',
  styleUrl: './cart-stepper-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideValueAccessor(CartStepperInputComponent, false),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always', appearance: 'outline', subscriptSizing: 'dynamic' },
    },
  ],
  host: {
    '[class.hide-hints]': 'hideHints()',
  },
})
export class CartStepperInputComponent implements ControlValueAccessor {
  private readonly _inputComponent = viewChild.required('quantityInput', { read: NumberInputComponent });

  public readonly control = input.required<IntegerFormControl>();
  public readonly label = input<string>('Quantity');
  public readonly placeholder = input<string>('');
  public readonly hideHints = input<boolean>(false);
  public readonly clearMode = input<boolean>(false);

  public readonly quantity = model<number | undefined>();
  public readonly decremented = output<void>();

  private _onChange: (value: number | null) => void = () => {};
  private _onTouched: () => void = () => {};

  protected get maxQuantity(): number {
    return this.control().options?.max ?? Infinity;
  }

  protected get minQuantity(): number {
    return this.control().options?.min ?? 0;
  }
  protected readonly decrementDisabled = computed(() => {
    const q = this.quantity() ?? null;
    if (this.clearMode()) {
      return q === null;
    }
    if (this.minQuantity >= 1) {
      return q === null || q <= this.minQuantity;
    }
    return q === null || q === 0;
  });

  protected readonly iconType = computed<'remove' | 'delete' | 'clear'>(() => {
    const q = this.quantity() ?? 0;
    if (this.clearMode()) {
      return q > this.minQuantity ? 'remove' : 'clear';
    }
    if (this.minQuantity >= 1 && (q === null || q <= this.minQuantity)) {
      return 'remove';
    }
    return q > 1 ? 'remove' : 'delete';
  });

  protected decrement(): void {
    const currentValue = this.quantity() ?? 0;

    // Emit decremented event before changing value
    this.decremented.emit();

    if (this.clearMode() && currentValue <= this.minQuantity) {
      // Clear mode at minimum: clear the quantity
      this.quantity.set(undefined);
    } else if (!this.clearMode() && currentValue <= this.minQuantity) {
      // Non-clear mode at minimum: don't decrement (handled by parent via decremented event)
      return;
    } else {
      // Normal decrement
      const step = this.control().options?.step ?? 1;
      const newValue = Math.max(currentValue - step, this.minQuantity);
      this.quantity.set(newValue);
    }
    this.control().markAsDirty();
    this.control().markAsTouched();
  }

  protected increment(): void {
    const currentValue = this.quantity() ?? 0;
    const step = this.control().options?.step ?? 1;
    const newValue = Math.min(currentValue + step, this.maxQuantity);
    this.quantity.set(newValue);
    this.control().markAsDirty();
    this.control().markAsTouched();
  }

  public focus(): void {
    setTimeout(() => untracked(this._inputComponent).field().nativeElement.focus());
  }

  constructor() {
    // Sync control valueChanges → quantity model + CVA onChange
    effect((onCleanup) => {
      const ctrl = this.control();

      // Initialize quantity from control only if quantity is undefined (hasn't been set yet)
      untracked(() => {
        const qtyValue = this.quantity();
        if (qtyValue === undefined && ctrl.value !== null) {
          this.quantity.set(ctrl.value ?? undefined);
        }
      });

      const sub = ctrl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
        this.quantity.set(value ?? undefined);
        this._onChange(value);
      });
      onCleanup(() => sub.unsubscribe());
    });

    // Sync quantity model → control
    effect(() => {
      const qty = this.quantity() ?? null;
      const ctrl = untracked(this.control);
      if (ctrl.value !== qty) {
        ctrl.setValue(qty, { emitEvent: false });
        ctrl.updateValueAndValidity();
      }
    });
  }

  // ControlValueAccessor
  writeValue(value: number | null): void {
    const ctrl = this.control();
    if (ctrl.value !== value) {
      ctrl.setValue(value ?? null, { emitEvent: false });
      ctrl.updateValueAndValidity();
    }
    this.quantity.set(value ?? undefined);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control().disable({ emitEvent: false });
    } else {
      this.control().enable({ emitEvent: false });
    }
  }
}
